// Centralized end-user error-message sanitizer.
//
// Two jobs:
//  1) NEVER leak internal/vendor names to end users. We use Shippo as our
//     carrier aggregator, but the user must never see the word "Shippo" (or
//     goshippo.com). Those are replaced with neutral, user-facing wording.
//  2) Turn raw/technical failures (bare "HTTP error 502", "Failed to fetch",
//     "[object Object]", empty) into a friendly, actionable message.
//
// Already-clean messages (e.g. "Your card was declined", a specific validation
// message) pass through unchanged. Use this everywhere an error string is shown
// to the user — the API clients call it so every surfaced message is covered.

// Internal vendor/provider names that must never be displayed to end users.
const INTERNAL_NAME_RE = /shippo|goshippo/i;

// Raw transport/technical messages that aren't meaningful to a user.
const RAW_TECHNICAL_RE = /^(HTTP error\b|HTTP \d|Request failed with status\b)|failed to fetch|networkerror|load failed|err_/i;

function friendlyForStatus(status) {
  if (status === 400 || status === 422) return 'Please check the information you entered and try again.';
  if (status === 401) return 'Your session has expired. Please log in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'We could not find what you were looking for.';
  if (status === 429) return 'You are doing that too often. Please wait a moment and try again.';
  // Gateway / origin-connectivity errors, incl. Cloudflare's 52x family, plus timeouts.
  if (status === 408 || status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 526)) {
    return 'The service is temporarily unavailable. Please try again in a moment.';
  }
  if (status >= 500) return 'Something went wrong on our end. Please try again.';
  return 'Something went wrong. Please try again.';
}

/**
 * Convert any error/message into a clean, end-user-facing string.
 * @param {string|object|null} message  raw message (or an Error/object)
 * @param {object} [opts]
 * @param {number} [opts.status]   HTTP status, used to pick a friendly fallback
 * @param {string} [opts.fallback] explicit fallback (overrides the status-based one)
 * @returns {string}
 */
export function toUserMessage(message, { status, fallback } = {}) {
  const friendly = fallback || friendlyForStatus(status);

  // Accept Error objects / nested shapes defensively.
  let text = '';
  if (typeof message === 'string') text = message;
  else if (message && typeof message.message === 'string') text = message.message;
  text = (text || '').trim();

  // Nothing usable, or a stringified object → friendly fallback.
  if (!text || text === '[object Object]' || text === 'undefined' || text === 'null') {
    return friendly;
  }

  // Bare technical/transport errors carry no user value → friendly fallback.
  if (RAW_TECHNICAL_RE.test(text)) return friendly;

  // Leaks an internal vendor name. If the whole thing is a raw provider error,
  // prefer the friendly fallback; otherwise strip the vendor name in place.
  if (INTERNAL_NAME_RE.test(text)) {
    if (/api error|malformed|object_|invalid token|unhandled/i.test(text)) return friendly;
    text = text
      .replace(/goshippo\.com/gi, 'the carrier')
      .replace(/\bgo-?shippo\b/gi, 'the carrier')
      .replace(/\bshippo\b/gi, 'the carrier');
  }

  return text;
}

export default toUserMessage;
